import { useEffect, useState } from "react";
import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Button, Checkbox,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Spacer
} from "@chakra-ui/react";
import { TbTrash } from "react-icons/tb";
import { useTranslation } from "react-i18next";

import Product, { NewProduct } from "./Product";
import { shortString } from "../../../utils/strings";
import { getAxiosInstance } from "../../../utils/axiosInstance";
import ScanProductSerialNumbers from "./ScanProductSerialNumbers";
import CategorySelect from "../../../components/CategorySelect";
import { NewProductWithPriceQuantity } from "../AddInventoryForm";

interface WithPriceAndQuantity {
  price: number;
  quantity: number;
}

export interface ProductWithPriceQuantity extends WithPriceAndQuantity {
  id: string;
  product: NewProduct;
}

interface IProductInventoryEntry {
  data: NewProductWithPriceQuantity | ProductWithPriceQuantity;
  delete: () => void;
  setData: (d: NewProductWithPriceQuantity | ProductWithPriceQuantity) => void;
  disableProduct?: boolean;
  unitOfMeasurement: { name: string, id: string }[];
  providerId?: string; // Just for existing products, is used to get the last acquisition price base on the selected provider
}

const ProductInventoryEntry = (props: IProductInventoryEntry) => {
  const { t } = useTranslation("translations");

  const fetchLastPrice = async (id: string) => {
    try {
      const res = await getAxiosInstance().get("/Products/LastPrice/" + id, { params: { providerId: props.providerId } })
      let tmp = { ...props.data };
      tmp.price = +res.data.price;
      props.setData(tmp);
    } catch (error) {
    }
  }

  useEffect(() => {
    if (props.data.quantity !== props.data.product.serialNumbers?.length) {
      const diff = props.data.quantity - (props.data.product.serialNumbers ? props.data.product.serialNumbers?.length : 0);

      if (diff >= 1) {
        // need to add new serials numbers
        const new_arr = new Array(diff).fill("");
        let tmp = { ...props.data };
        tmp.product.serialNumbers = tmp.product.serialNumbers ? [...tmp.product.serialNumbers, ...new_arr] : new_arr;
        props.setData(tmp);
      } else if (diff < 0) {
        // need to remove some serial numbers
        let tmp = { ...props.data };
        tmp.product.serialNumbers = tmp.product.serialNumbers ? [...tmp.product.serialNumbers].slice(0, props.data.quantity) : [];
        props.setData(tmp);
      }

    }
  }, [props.data]);

  useEffect(() => {
    if ((props.data as ProductWithPriceQuantity).id) {
      fetchLastPrice((props.data as ProductWithPriceQuantity).id);
    }
  }, [props.providerId]);

  return (
    <AccordionItem>
      <AccordionButton>
        <Button
          onClick={() => props.delete()}
          colorScheme="red"
          variant="solid"
          mr="2"
        >
          <TbTrash />
        </Button>
        {
          `${props.data.product.name.length > 0 ?
            shortString(props.data.product.name) :
            t("NEW_PRODUCT_NO")} (${t("QUANTITY_NEW_PRODUCT_TMP")} ${props.data.quantity}) (${t("PRICE_NEW_PRODUCT_TMP")}: ${props.data.price})`
        }
        <Spacer />
        <AccordionIcon />
      </AccordionButton>
      <AccordionPanel>
        <Product
          data={props.data.product}
          onChange={(data) => props.setData({ ...props.data, product: data })}
          disable={props.disableProduct}
          unitOfMeasurements={props.unitOfMeasurement}
          hideReset={true}
          afterNameComponent={
            (props.data as NewProductWithPriceQuantity).subCategories &&
            <CategorySelect
              customClass="my-4"
              isRequired={true}
              mainCategory={(props.data as NewProductWithPriceQuantity).mainCategory}
              subCategory={(props.data as NewProductWithPriceQuantity).subCategories}
              onCategorySelect={(data) => {
                let tmp = { ...props.data } as NewProductWithPriceQuantity;
                tmp.mainCategory = data;
                props.setData({ ...tmp });
              }}
              onSubCategorySelect={(data) => {
                let tmp = { ...props.data } as NewProductWithPriceQuantity;
                tmp.subCategories = data;
                props.setData({ ...tmp });
              }}
            />
          }
        />
        <HStack>
          <FormControl>
            <FormLabel>{t("NEW_PRODUCT_QUANTITY")}</FormLabel>
            <Input
              isRequired
              type="number"
              inputMode="numeric"
              name='product_quantity'
              value={props.data.quantity}
              onChange={(e) => {
                let data = { ...props.data };
                let newVal = +e.target.value;
                data.quantity = newVal >= 0 ? newVal : 0;
                props.setData(data);
              }}
              placeholder={t("NEW_PRODUCT_QUANTITY_PLACEHOLDER")}
            />
          </FormControl>
          <FormControl>
            <FormLabel>{t("NEW_PRODUCT_PRICE")}</FormLabel>
            <Input
              isRequired
              type="number"
              inputMode="numeric"
              name='product_price'
              value={props.data.price}
              onChange={(e) => {
                let data = { ...props.data };
                data.price = +e.target.value;
                props.setData(data);
              }}
              placeholder={t("NEW_PRODUCT_PRICE_PLACEHOLDER")}
            />
          </FormControl>
        </HStack>
        <FormControl className="flex flex-row my-2 mt-4">
          <Checkbox
            isDisabled={props.disableProduct}
            className="mr-[5px]"
            isChecked={props.data.product.hasSerialNumber}
            onChange={() => {
              let tmp = { ...props.data };
              tmp.product.hasSerialNumber = !tmp.product.hasSerialNumber;
              props.setData(tmp);
            }}
          />
          <FormLabel className="mt-[5px]">{t("PRODUCT_WITH_SERIAL_NUMBER")}</FormLabel>
        </FormControl>
        {props.data.product.hasSerialNumber &&
          <ScanProductSerialNumbers
            serialNumbers={props.data.product.serialNumbers ?? []}
            onSerialNumbersChange={(values: string[]) => {
              let tmp = { ...props.data };
              tmp.product.serialNumbers = values;
              props.setData(tmp);
            }}
          />
        }
      </AccordionPanel>

    </AccordionItem>
  )
}

export default ProductInventoryEntry;